"use client";

import { useState, useEffect } from "react";
import { useTheme } from "@/contexts/theme-context";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, Menu, X, Sun, Moon, Check, Zap, Star, TrendingUp, Globe, Circle } from "lucide-react";
import { type PlanConfig } from "@/lib/stripe";
import { usePlans } from "@/hooks/use-plans";
import { Button } from "@/components/ui/button";

// Landing components
import { GradientBackground } from "@/components/landing/gradient-background";
import { PlatformIconsGrid } from "@/components/landing/platform-icons";
import { InclinedSlices } from "@/components/landing/inclined-slices";
import { StatsSection } from "@/components/landing/stats-section";
import { ProcessDiagram } from "@/components/landing/process-diagram";
import { AnalyticsPreview } from "@/components/landing/analytics-preview";
import { TestimonialsCarousel } from "@/components/landing/testimonials-carousel";
import { ContactSection } from "@/components/landing/contact-form";
import { ContentGallery } from "@/components/landing/content-gallery";
import { CookieBanner } from "@/components/cookie-banner";
import { AFFILIATES_ENABLED } from "@/lib/feature-flags";

export default function LandingPage() {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { plans: PLANS } = usePlans();
  const isLightTheme = theme === "light";

  // Smooth scroll with custom alignment per section
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          const element = document.querySelector(href);
          if (element) {
            // Sections that should be centered (accounting for navbar)
            const centeredSections = ['#why-us', '#featured', '#testimonials', '#pricing', '#contact'];
            // Sections that should scroll to top with offset
            const topAlignedSections = ['#how-it-works', '#toolkit', '#gallery'];

            if (centeredSections.includes(href)) {
              // Center the section in viewport accounting for sticky navbar
              const navbarHeight = 80;
              const elementRect = element.getBoundingClientRect();
              const elementTop = elementRect.top + window.pageYOffset;
              const elementHeight = elementRect.height;
              const viewportHeight = window.innerHeight;

              // Calculate true center: element should be centered in the visible viewport (below navbar)
              const availableViewportHeight = viewportHeight - navbarHeight;
              const targetScroll = elementTop - navbarHeight - (availableViewportHeight - elementHeight) / 2;

              window.scrollTo({
                top: targetScroll,
                behavior: 'smooth'
              });
            } else if (topAlignedSections.includes(href)) {
              // Scroll to top of section with minimal navbar offset
              const navbarHeight = 64; // actual navbar height
              const minimalSpace = 2; // minimal breathing room
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - navbarHeight - minimalSpace;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            } else {
              // Default smooth scroll
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  const menuLinks = [
    { href: "#why-us", label: "Why Us" },
    { href: "#featured", label: "Featured" },
    { href: "#how-it-works", label: "How it Works" },
    { href: "#toolkit", label: "Toolkit" },
    { href: "#gallery", label: "Gallery" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#pricing", label: "Pricing" },
    { href: "#contact", label: "Get in Touch" },
    { href: "/contact", label: "Contact", isExternal: true },
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden landing-theme">
      {/* ============================================ */}
      {/* NAVIGATION */}
      {/* ============================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - proportional sizing */}
            <Link href="/" className="flex items-center" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              <span className="text-xl portrait:text-[clamp(1.25rem,2.5vw,1.75rem)] font-bold text-foreground tracking-tight">Lateral</span>
            </Link>

            {/* Separator - Desktop only (lg+) */}
            <div className="hidden lg:block separator-line mx-6" />

            {/* Menu Items - Desktop only (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-6 portrait:gap-[clamp(1.5rem,2vw,2rem)] flex-1">
              {menuLinks.map((link) => (
                link.isExternal ? (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="text-sm portrait:text-[clamp(0.875rem,1.2vw,1rem)] text-foreground-secondary hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-sm portrait:text-[clamp(0.875rem,1.2vw,1rem)] text-foreground-secondary hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </a>
                )
              ))}
            </div>

            {/* Auth Buttons + Theme Toggle - Desktop (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-3 portrait:gap-[clamp(0.75rem,1.2vw,1rem)]">
              {/* Theme Toggle - Desktop */}
              <button
                onClick={toggleTheme}
                className="p-2 portrait:p-[clamp(0.5rem,0.8vw,0.75rem)] rounded-lg text-foreground-secondary hover:text-foreground hover:bg-white/5 transition-all"
                aria-label="Toggle theme"
              >
                {isLightTheme ? <Moon className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" /> : <Sun className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />}
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="portrait:text-[clamp(0.875rem,1.2vw,1rem)] portrait:h-[clamp(2.25rem,3.5vh,2.75rem)] portrait:px-[clamp(1rem,1.5vw,1.5rem)]"
                onClick={() => router.push('/login')}
              >
                Log In
              </Button>
              <span className="text-foreground-muted text-sm select-none inline-flex items-center h-8 pr-3">or</span>
              <Button
                variant="primary"
                size="sm"
                className="portrait:text-[clamp(0.875rem,1.2vw,1rem)] portrait:h-[clamp(2.25rem,3.5vh,2.75rem)] portrait:px-[clamp(1rem,1.5vw,1.5rem)]"
                onClick={() => router.push('/signup')}
              >
                Sign Up
              </Button>
            </div>

            {/* Mobile/Tablet: Theme Toggle + Hamburger (below lg) */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Theme Toggle - Mobile */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-foreground-secondary hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {isLightTheme ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground hover:text-foreground-secondary transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Menu Dropdown - Overlay (below lg) */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border/30 shadow-lg"
            >
              <div className="py-6 flex flex-col items-center gap-4">
                {menuLinks.map((link) => (
                  link.isExternal ? (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-base text-foreground-secondary hover:text-foreground transition-colors py-2"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-base text-foreground-secondary hover:text-foreground transition-colors py-2"
                    >
                      {link.label}
                    </a>
                  )
                ))}
                <div className="border-t border-border/30 w-32 my-2" />
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      router.push('/login');
                    }}
                  >
                    Log In
                  </Button>
                  <span className="text-foreground-muted text-sm select-none inline-flex items-center h-8 pr-3">or</span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      router.push('/signup');
                    }}
                  >
                    Sign Up
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============================================ */}
      {/* SECTION 1: HERO */}
      {/* ============================================ */}
      <section className="relative min-h-screen flex flex-col pt-[73px]">
        {/* Hero gradient background image - responsive (Dark Theme) */}
        <picture className="absolute inset-0 pointer-events-none dark-only">
          <source media="(min-width: 1024px) and (min-aspect-ratio: 1/1)" srcSet="/hero-bg-desktop.jpg" />
          <source media="(min-width: 768px) and (min-aspect-ratio: 1/1)" srcSet="/hero-bg-tablet.jpg" />
          <img
            src="/hero-bg-mobile.jpg"
            alt=""
            className="w-full h-full object-cover object-left min-[440px]:object-[50%_62%]"
          />
        </picture>
        {/* Hero gradient background image - responsive (Light Theme) */}
        <picture className="absolute inset-0 pointer-events-none light-only z-[1]" style={{ backgroundColor: "var(--background)" }}>
          <source media="(min-width: 1024px) and (min-aspect-ratio: 1/1)" srcSet="/hero-bg-desktop-light.jpg" />
          <source media="(min-width: 768px) and (min-aspect-ratio: 1/1)" srcSet="/hero-bg-tablet-light.jpg" />
          <img
            src="/hero-bg-mobile-light.jpg"
            alt=""
            className="w-full h-full object-cover object-left min-[440px]:object-[50%_62%]"
          />
        </picture>

        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col flex-1 w-full">
          {/* PORTRAIT MODE: Right-aligned design (mobile layout for tall screens) */}
          <div className="portrait:flex landscape:hidden flex-1 flex items-center justify-end hero-section-320 hero-section-375-480" style={{ marginTop: 'clamp(-5rem, -10vh, -2rem)' }}>
            <div className="text-right" style={{ maxWidth: 'clamp(300px, 65vw, 700px)' }}>
              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-bold text-foreground leading-tight mb-[clamp(0.75rem,2vh,1.25rem)]"
              >
                <span className="hero-heading-primary block mb-[clamp(0.25rem,0.8vh,0.5rem)]">AI that does</span>
                <span className="text-gradient-hero hero-heading-secondary block">Marketing for you</span>
              </motion.h1>

              {/* Subheadline - SMALL for 2 lines max */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hero-subheading text-foreground-secondary mb-[clamp(1.25rem,3vh,2rem)]"
                style={{ maxWidth: 'clamp(205px, 40vw, 350px)', marginLeft: 'auto', textAlign: 'justify', textAlignLast: 'justify' }}
              >
                AI content creation and<br />scheduling made simple.
              </motion.p>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex justify-end"
              >
                <Button
                  size="lg"
                  className="hero-button h-[clamp(2.75rem,6vh,3.5rem)] px-[clamp(1.5rem,3.5vw,2.25rem)] text-[clamp(1rem,1.8vw,1.25rem)]"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                  onClick={() => router.push('/signup?plan=trial')}
                >
                  Start Free Trial
                </Button>
              </motion.div>
            </div>
          </div>

          {/* LANDSCAPE MODE: Centered design (desktop layout for wide screens) */}
          <div className="portrait:hidden landscape:flex flex-1 flex items-center justify-center" style={{ marginTop: 'clamp(-10rem, -18vh, -6rem)' }}>
            <div className="text-center" style={{ maxWidth: 'clamp(520px, 62vw, 920px)' }}>
              {/* Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-bold text-foreground leading-tight mb-[clamp(1rem,2vh,1.5rem)]"
              >
                <span className="hero-heading-primary block mb-[clamp(0.5rem,1vh,0.75rem)]">AI that does</span>
                <span className="text-gradient-hero hero-heading-secondary block">Marketing for you</span>
              </motion.h1>

              {/* Subheadline - shortened for 2 lines max */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="hero-subheading text-foreground-secondary mb-[clamp(1.75rem,3.5vh,2.75rem)] mx-auto"
                style={{ maxWidth: 'clamp(360px, 46vw, 620px)' }}
              >
                Generate stunning content, schedule posts, and grow your brand across all platforms. Powered by the most advanced AI models.
              </motion.p>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex justify-center"
              >
                <Button
                  size="lg"
                  className="h-[clamp(2.75rem,4vh,3.25rem)] px-[clamp(1.5rem,2.5vw,2rem)] text-[clamp(0.875rem,1vw,1.125rem)]"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                  onClick={() => router.push('/signup?plan=trial')}
                >
                  Start Free Trial
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Platform Icons - adapts to portrait/landscape */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="pb-[clamp(2rem,5vh,4rem)]"
            style={{ marginTop: 'clamp(-6rem, -8vh, -2rem)' }}
          >
            <PlatformIconsGrid />
          </motion.div>
        </div>
      </section>

      {/* Spacer between Hero and Why Us */}
      <div style={{ height: 'clamp(2rem, 5vh, 4rem)' }} />

      {/* ============================================ */}
      {/* SECTION 2: WHY TEAMS RELY ON US (Inclined Slices) */}
      {/* ============================================ */}
      <section id="why-us">
        <InclinedSlices />
      </section>

      {/* ============================================ */}
      {/* SECTION 3: STATS/METRICS */}
      {/* ============================================ */}
      <StatsSection />

      {/* ============================================ */}
      {/* SECTION 4: CTA BANNER */}
      {/* ============================================ */}
      <section className="relative" style={{ paddingTop: 'clamp(5rem, 12vh, 10rem)', paddingBottom: 'clamp(5rem, 12vh, 10rem)' }}>
        {/* Accentuated gradient background - no card */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="w-[1200px] h-[600px]"
            style={{
              background: `
                radial-gradient(ellipse 70% 50% at 50% 50%, rgba(16, 185, 129, 0.25) 0%, transparent 60%),
                radial-gradient(ellipse 50% 40% at 30% 40%, rgba(163, 230, 53, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse 50% 40% at 70% 60%, rgba(5, 150, 105, 0.2) 0%, transparent 50%)
              `,
              filter: "blur(40px)",
            }}
          />
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Mobile version */}
            <h2 className="md:hidden text-2xl min-[420px]:text-[1.75rem] min-[500px]:text-3xl sm:text-[2rem] font-bold text-foreground mb-8 text-balance">
              <span className="text-gradient">Accelerate</span> your growth
              <br />
              with <span className="relative inline-block">
                Lateral
                <svg
                  className="absolute -bottom-1 left-0 w-full h-2"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 6C60 3 140 9 198 5"
                    stroke="url(#underline-grad-mobile)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="underline-grad-mobile" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#a3e635" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h2>

            {/* Desktop version */}
            <h2 className="hidden md:block text-[2.25rem] lg:text-[2.5rem] xl:text-5xl font-bold text-foreground mb-8 text-balance">
              Find your <span className="text-gradient">shortest path</span>
              <br />
              to growth with <span className="relative inline-block">
                Lateral
                <svg
                  className="absolute -bottom-2 left-0 w-full h-3"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 6C60 3 140 9 198 5"
                    stroke="url(#underline-grad-desktop)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="underline-grad-desktop" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#a3e635" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h2>
            <Button
              size="lg"
              className="h-10 px-5 text-sm sm:h-11 sm:px-6 md:h-12 md:px-7 md:text-base"
              rightIcon={<ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />}
              onClick={() => router.push('/signup?plan=trial')}
            >
              Start Free Trial
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* SECTION 6: PROCESS DIAGRAM (How it Works) */}
      {/* ============================================ */}
      <section id="how-it-works">
        <ProcessDiagram />
      </section>

      {/* ============================================ */}
      {/* SECTION 8: ANALYTICS DASHBOARD PREVIEW */}
      {/* ============================================ */}
      <section id="toolkit" className="scroll-mt-32">
        <AnalyticsPreview />
      </section>

      {/* ============================================ */}
      {/* SECTION 9: CONTENT GALLERY */}
      {/* ============================================ */}
      <section id="gallery" className="scroll-mt-32">
        <ContentGallery />
      </section>

      {/* ============================================ */}
      {/* SECTION 10: SECONDARY CTA */}
      {/* Responsive breakpoints: xs(320), sm(640), md(768), lg(1024), xl(1440) */}
      {/* ============================================ */}
      <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(5rem, 12vh, 10rem)', paddingBottom: 'clamp(5rem, 12vh, 10rem)' }}>
        {/* Center gradient background - responsive sizing */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="w-[400px] sm:w-[600px] md:w-[800px] lg:w-[900px] h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px]"
            style={{
              background: `radial-gradient(ellipse at 50% 50%, rgba(16, 185, 129, 0.3) 0%, transparent 55%)`,
              filter: "blur(50px)",
            }}
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Heading - 2 responsive variations matching first CTA */}
            {/* Mobile (below md/768px) */}
            <h2 className="md:hidden text-2xl min-[420px]:text-[1.75rem] min-[500px]:text-3xl sm:text-[2rem] leading-tight font-bold text-foreground mb-4 text-balance">
              Marketing on{" "}
              <span className="text-gradient">autopilot</span>
            </h2>
            {/* Desktop md+ (768px+) - matches first CTA sizing */}
            <h2 className="hidden md:block text-[2.25rem] lg:text-[2.5rem] xl:text-5xl leading-tight font-bold text-foreground mb-4 text-balance max-w-md lg:max-w-lg xl:max-w-xl mx-auto">
              Stop wasting time on{" "}
              <span className="text-gradient">manual marketing</span>
            </h2>

            {/* Body text - 2 responsive variations */}
            {/* Mobile (below md/768px) */}
            <p className="md:hidden text-sm sm:text-[0.9375rem] leading-relaxed text-foreground-secondary mb-6 sm:mb-7 max-w-[280px] sm:max-w-sm mx-auto text-pretty">
              Let AI handle the heavy lifting while you focus on growing your business.
            </p>
            {/* Desktop md-lg (768-1024px) - shorter text */}
            <p className="hidden md:block lg:hidden text-base leading-relaxed text-foreground-secondary mb-8 max-w-sm mx-auto text-balance">
              Let AI handle the heavy lifting while you focus on growing your business.
            </p>
            {/* Desktop lg+ (1024px+) - full text */}
            <p className="hidden lg:block text-lg leading-relaxed text-foreground-secondary mb-8 max-w-md xl:max-w-lg mx-auto text-balance">
              Every day you wait is another day of missed opportunities. Let AI handle the heavy lifting while you focus on growing your business.
            </p>

            <div className="flex items-center justify-center">
              <Button
                size="lg"
                className="h-10 px-5 text-sm sm:h-11 sm:px-6 md:h-12 md:px-7 md:text-base"
                rightIcon={<ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />}
                onClick={() => router.push('/signup?plan=trial')}
              >
                Start Free Trial
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* SECTION 10: TESTIMONIALS */}
      {/* ============================================ */}
      <section id="testimonials">
        <TestimonialsCarousel />
      </section>

      {/* ============================================ */}
      {/* SECTION 11: PRICING */}
      {/* ============================================ */}
      <section id="pricing" className="scroll-mt-24 relative py-28 overflow-hidden">
        {/* Background — two layered glows for depth, not just one flat circle */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full bg-[#10b981]/[0.03] blur-[140px]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-[#10b981]/[0.02] blur-[100px]" />
        </div>

        <div className="relative max-w-[1120px] mx-auto px-6">
          {/* Header — keeping it tight and confident */}
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-[12px] font-semibold text-[#10b981] uppercase tracking-[0.2em] mb-4"
            >
              Pricing
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.06 }}
              className="text-2xl md:text-4xl font-bold text-foreground mb-5"
            >
              Start free. Scale as you <span className="text-gradient">grow.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[15px] text-foreground-secondary max-w-md mx-auto leading-relaxed"
            >
              No credit card required to start. Upgrade anytime when you&apos;re ready.
            </motion.p>
          </div>

          {/* Plan grid — more breathing room, cards need space to feel premium */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {([
              { key: "trial" as const, accent: "white" as const, cta: "Start free" },
              { key: "growth" as const, accent: "emerald" as const, cta: "Get started" },
              { key: "agency" as const, accent: "emerald" as const, featured: true, badge: "Most Popular", cta: "Get started" },
              { key: "scale" as const, accent: "purple" as const, cta: "Contact sales" },
            ] as const).map((item, idx) => {
              const p = PLANS[item.key];
              const inf = (v: number) => v === Infinity || v >= 999999;
              const features: { text: string; ok: boolean }[] = item.key === "scale"
                ? [
                    { text: "Unlimited videos & images", ok: true },
                    { text: "Unlimited AI editing", ok: true },
                    { text: "Unlimited brands & seats", ok: true },
                    { text: "Unlimited exports", ok: true },
                    { text: "Publish to all platforms", ok: true },
                    { text: "API & ecommerce integrations", ok: true },
                    { text: "Priority support + onboarding", ok: true },
                  ]
                : item.key === "trial"
                ? [
                    { text: `${p?.videos ?? 0} AI videos`, ok: true },
                    { text: `${p?.images ?? 0} AI images`, ok: true },
                    { text: "Unlimited AI editing", ok: true },
                    { text: `${p?.brands} brand`, ok: true },
                    { text: inf(p?.exportsPerMonth ?? 0) ? "Unlimited exports" : `${p?.exportsPerMonth ?? 0} exports / day`, ok: true },
                    { text: "Unlimited publishing", ok: true },
                    { text: "Unlimited reposting", ok: true },
                    { text: "Social audit", ok: false },
                  ]
                : [
                    { text: `${p?.videos ?? 0} AI videos / month`, ok: true },
                    { text: `${p?.images ?? 0} AI images / month`, ok: true },
                    { text: "Unlimited AI editing", ok: true },
                    { text: inf(p?.brands ?? 0) ? `Unlimited brands · ${p?.users} users` : p?.users > 1 ? `${p?.brands} brand${p?.brands > 1 ? "s" : ""} · ${p?.users} users` : `${p?.brands} brand`, ok: true },
                    { text: inf(p?.exportsPerMonth ?? 0) ? "Unlimited exports" : `${p?.exportsPerMonth ?? 0} exports / month`, ok: true },
                    { text: "Unlimited publishing", ok: true },
                    { text: "Unlimited reposting", ok: true },
                    { text: p?.socialAuditFrequency === "weekly" ? "Weekly social audit" : p?.socialAuditFrequency === "monthly" ? "Monthly social audit" : "Social audit", ok: !!p?.socialAuditFrequency },
                  ];
              const plan = p;
              const isFeatured = "featured" in item && item.featured;
              const isEmerald = item.accent === "emerald";
              const isPurple = item.accent === "purple";

              return (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08, duration: 0.5 }}
                  className="relative group"
                >
                  {/* Outer glow on featured card — draws the eye */}
                  {isFeatured && (
                    <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-[#10b981]/40 via-[#10b981]/10 to-transparent opacity-60 blur-[1px]" />
                  )}

                  <div
                    className={`relative h-full rounded-2xl border px-5 py-5 flex flex-col overflow-hidden transition-all duration-300 ${
                      isFeatured
                        ? "border-[#10b981]/30 bg-[#10b981]/[0.04]"
                        : isPurple
                        ? "border-purple-500/15 bg-white/[0.02] hover:border-purple-500/25"
                        : isEmerald
                        ? "border-white/[0.08] bg-white/[0.02] hover:border-[#10b981]/20"
                        : "border-white/[0.06] bg-white/[0.015] hover:border-white/[0.12]"
                    }`}
                  >
                    {/* Ambient gradient — top-left warm glow like reference card */}
                    {isFeatured ? (
                      <>
                        {/* Lime blob — top-left */}
                        <div
                          className="absolute -top-[15%] -left-[15%] w-[80%] h-[75%] pointer-events-none"
                          style={{
                            background: "radial-gradient(circle at center, rgba(190,242,100,0.45) 0%, rgba(190,242,100,0.15) 50%, transparent 75%)",
                            filter: "blur(25px)",
                          }}
                        />
                        {/* Emerald blob — overlapping, shifted down-right */}
                        <div
                          className="absolute top-[0%] left-[5%] w-[70%] h-[70%] pointer-events-none"
                          style={{
                            background: "radial-gradient(circle at center, rgba(16,185,129,0.40) 0%, rgba(16,185,129,0.12) 50%, transparent 75%)",
                            filter: "blur(25px)",
                          }}
                        />
                      </>
                    ) : (
                      <div
                        className="absolute -top-[20%] -left-[20%] w-[75%] h-[75%] pointer-events-none"
                        style={{
                          background: isPurple
                            ? "radial-gradient(circle at center, rgba(168,85,247,0.25) 0%, rgba(168,85,247,0.08) 40%, transparent 70%)"
                            : isEmerald
                            ? "radial-gradient(circle at center, rgba(180,160,50,0.18) 0%, rgba(16,185,129,0.15) 45%, transparent 70%)"
                            : "radial-gradient(circle at center, rgba(180,160,50,0.12) 0%, rgba(16,185,129,0.08) 45%, transparent 70%)",
                          filter: "blur(40px)",
                        }}
                      />
                    )}

                    {/* Popular — typographic watermark, top-right */}
                    {"badge" in item && item.badge && (
                      <span
                        className="absolute top-[15px] -right-[8px] text-[42px] font-bold leading-none tracking-tight select-none pointer-events-none dark:opacity-50"
                        style={{ color: "var(--section-index-color)", writingMode: "vertical-rl" }}
                      >
                        Popular
                      </span>
                    )}

                    {/* Plan name */}
                    <div className="mb-4 relative z-10">
                      <span className="text-[15px] font-semibold text-foreground">{plan.name}</span>
                    </div>

                    {/* Price */}
                    <div className="mb-5 relative z-10">
                      <span className="text-[32px] font-bold tracking-tight text-foreground">{plan.price}</span>
                      <span className="text-[13px] text-foreground ml-1.5">
                        {["trial", "free"].includes(item.key) ? `· ${p?.trialDays ?? 14}-day trial` : `/ month`}
                      </span>
                    </div>

                    {/* Separator */}
                    <div className={`h-px mb-4 ${
                      isFeatured ? "bg-[#10b981]/15" : isPurple ? "bg-purple-500/10" : "bg-white/[0.06]"
                    }`} />

                    {/* Features */}
                    <ul className="space-y-2 mb-5 flex-1 relative z-10">
                      {features.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-2.5">
                          {f.ok ? (
                            <Check className="w-3.5 h-3.5 text-[#10b981] shrink-0 mt-[2px]" />
                          ) : (
                            <Circle className="w-3.5 h-3.5 text-white/20 shrink-0 mt-[2px]" />
                          )}
                          <span className={`text-[13px] leading-snug ${f.ok ? "text-foreground-secondary" : "text-white/25"}`}>
                            {f.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA — all green buttons get white text. Growth gets solid fill, others get variants. */}
                    {item.key === "scale" ? (
                      <a
                        href="mailto:hello@lateralteams.com?subject=Lateral Scale Plan Inquiry"
                        className="relative z-10 w-full h-10 rounded-xl text-[13px] font-semibold flex items-center justify-center transition-all bg-purple-500/15 hover:bg-purple-500/25 text-white/80 border border-purple-500/20 hover:border-purple-500/30"
                      >
                        {item.cta}
                      </a>
                    ) : item.key === "trial" ? (
                      <button
                        onClick={() => router.push("/signup")}
                        className="relative z-10 w-full h-10 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-all bg-white/[0.06] hover:bg-white/[0.10] text-white/80 border border-white/[0.08] hover:border-white/[0.14] cursor-pointer"
                      >
                        {item.cta}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => router.push("/signup")}
                        className={`relative z-10 w-full h-10 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                          isFeatured
                            ? "bg-[#10b981] hover:bg-[#0ea572] shadow-lg shadow-[#10b981]/20"
                            : "bg-[#10b981]/80 hover:bg-[#10b981]"
                        }`}
                        style={{ color: "white" }}
                      >
                        {item.cta}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Extra-brand addon note removed: it had a hardcoded
                        white inline color that vanished in light theme,
                        and with the note gone every card has the same
                        bottom shape (CTA only) so buttons naturally align
                        across the row without any placeholder. The addon
                        is still surfaced inside Settings → Billing where
                        it actually matters; landing doesn't need to teach
                        it. */}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center text-[13px] text-foreground-secondary mt-10"
          >
            Start free — {PLANS.free?.trialDays ?? PLANS.trial?.trialDays ?? 14}-day free trial on the Free plan. Cancel anytime. No hidden fees.
          </motion.p>
        </div>
      </section>

      {/* ============================================ */}
      {/* SECTION 12: CONTACT FORM */}
      {/* ============================================ */}
      <section id="contact">
        <ContactSection />
      </section>

      {/* Spacer between Contact and Footer */}
      <div style={{ height: 'clamp(3rem, 6vh, 5rem)' }} />

      {/* ============================================ */}
      {/* SECTION 12: FOOTER */}
      {/* ============================================ */}
      <footer className="relative border-t border-border pt-6 pb-0">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Top */}
          <div className="flex justify-end mb-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-sm text-foreground-secondary hover:text-foreground transition-colors flex items-center gap-1"
            >
              Back to top <span className="text-base">↑</span>
            </a>
          </div>

          {/* Footer content - responsive structure */}
          {/* Mobile & Tablet (< lg): Centered layout */}
          <div className="lg:hidden flex flex-col items-center gap-6 pb-6">
            <Link href="/" className="flex items-center" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
            </Link>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              <a href="#why-us" className="hover:text-foreground transition-colors">Why Us</a>
              <a href="#featured" className="hover:text-foreground transition-colors">Featured</a>
              <a href="#how-it-works" className="hover:text-foreground transition-colors">How it Works</a>
              <a href="#toolkit" className="hover:text-foreground transition-colors">Toolkit</a>
              <a href="#gallery" className="hover:text-foreground transition-colors">Gallery</a>
              <a href="#testimonials" className="hover:text-foreground transition-colors">Testimonials</a>
              <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
              <a href="#contact" className="hover:text-foreground transition-colors">Get in Touch</a>
            </div>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
              <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              {AFFILIATES_ENABLED && (
                <Link href="/affiliate" className="hover:text-foreground transition-colors">Affiliates</Link>
              )}
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm text-foreground-secondary">
                20-22 Wenlock Road, London, UK
              </p>
              <p className="text-sm text-foreground-secondary">
                info@lateralteams.com
              </p>
            </div>
            <p className="text-sm text-foreground-muted text-center">
              © 2026 Lateral Teams · All rights reserved
            </p>
          </div>

          {/* Desktop (lg+): Multi-row layout */}
          <div className="hidden lg:block pb-6">
            {/* Top row: Logo + Main Nav */}
            <div className="flex items-center gap-8 mb-6">
              <Link href="/" className="flex items-center shrink-0" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
              </Link>
              <div className="flex items-center gap-5 text-sm text-foreground-secondary">
                <a href="#why-us" className="hover:text-foreground transition-colors">Why Us</a>
                <a href="#featured" className="hover:text-foreground transition-colors">Featured</a>
                <a href="#how-it-works" className="hover:text-foreground transition-colors">How it Works</a>
                <a href="#toolkit" className="hover:text-foreground transition-colors">Toolkit</a>
                <a href="#gallery" className="hover:text-foreground transition-colors">Gallery</a>
                <a href="#testimonials" className="hover:text-foreground transition-colors">Testimonials</a>
                <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
                <a href="#contact" className="hover:text-foreground transition-colors">Get in Touch</a>
              </div>
            </div>
            {/* Bottom row: Legal + Company Info + Copyright */}
            <div className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-5 text-sm text-foreground-secondary">
                <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
                <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
                <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
                <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
                {AFFILIATES_ENABLED && (
                  <Link href="/affiliate" className="hover:text-foreground transition-colors">Affiliates</Link>
                )}
              </div>
              <div className="flex items-center gap-6 text-sm text-foreground-secondary">
                <span>20-22 Wenlock Road, London, UK</span>
                <span>·</span>
                <span>info@lateralteams.com</span>
              </div>
              <p className="text-sm text-foreground-muted shrink-0">
                © 2026 Lateral Teams · All rights reserved
              </p>
            </div>
          </div>
        </div>

        {/* Large Lateral text - 60% visible, 40% hidden - consistent on all screens */}
        <div className="relative h-[14vw] mt-4 overflow-hidden">
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-[-55%] text-[23vw] font-bold leading-none tracking-[0.02em] select-none pointer-events-none whitespace-nowrap"
            style={{ color: "var(--watermark-color)" }}
          >
            Lateral
          </span>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
}
